import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  Timestamp,
  increment
} from 'firebase/firestore';
import { db } from './config';
import { SavedStory, StoryVersion, StoryFilters } from '../../types/story';
import { ViralStory } from '../documentaryAI';

const STORIES_COLLECTION = 'stories';

// Convert ViralStory to StoryVersion
export const createStoryVersion = (
  story: ViralStory, 
  versionNumber: number = 1,
  changeDescription?: string
): StoryVersion => {
  return {
    id: `v${versionNumber}_${Date.now()}`,
    versionNumber,
    fullStory: story.fullStory,
    parts: story.parts,
    metadata: story.metadata,
    createdAt: new Date(),
    changeDescription
  };
};

// Save a new story
export const saveStory = async (
  userId: string,
  topic: string,
  ideaTitle: string,
  ideaDescription: string,
  story: ViralStory
): Promise<string> => {
  try {
    const storyId = `story_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const initialVersion = createStoryVersion(story, 1, 'Initial version');
    
    const savedStory: SavedStory = {
      id: storyId,
      userId,
      topic,
      ideaTitle,
      ideaDescription,
      currentVersion: initialVersion,
      versions: [initialVersion],
      createdAt: new Date(),
      updatedAt: new Date(),
      lastAutoSave: new Date(),
      tags: [],
      isFavorite: false,
      viewCount: 0
    };

    await setDoc(doc(db, STORIES_COLLECTION, storyId), {
      ...savedStory,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastAutoSave: serverTimestamp()
    });

    return storyId;
  } catch (error) {
    console.error('Error saving story:', error);
    throw error;
  }
};

// Update existing story with new version
export const updateStoryWithVersion = async (
  storyId: string,
  story: ViralStory,
  changeDescription: string
): Promise<void> => {
  try {
    const storyRef = doc(db, STORIES_COLLECTION, storyId);
    const storyDoc = await getDoc(storyRef);
    
    if (!storyDoc.exists()) {
      throw new Error('Story not found');
    }
    
    const existingStory = storyDoc.data() as SavedStory;
    const newVersionNumber = existingStory.versions.length + 1;
    const newVersion = createStoryVersion(story, newVersionNumber, changeDescription);
    
    await updateDoc(storyRef, {
      currentVersion: newVersion,
      versions: [...existingStory.versions, newVersion],
      updatedAt: serverTimestamp(),
      lastAutoSave: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating story:', error);
    throw error;
  }
};

// Autosave story (updates lastAutoSave without creating new version)
export const autosaveStory = async (
  storyId: string,
  story: ViralStory
): Promise<void> => {
  try {
    const storyRef = doc(db, STORIES_COLLECTION, storyId);
    const storyDoc = await getDoc(storyRef);
    
    if (!storyDoc.exists()) {
      throw new Error('Story not found');
    }
    
    const existingStory = storyDoc.data() as SavedStory;
    
    // Update current version with latest content
    const updatedCurrentVersion = {
      ...existingStory.currentVersion,
      fullStory: story.fullStory,
      parts: story.parts,
      metadata: story.metadata
    };
    
    await updateDoc(storyRef, {
      currentVersion: updatedCurrentVersion,
      lastAutoSave: serverTimestamp()
    });
  } catch (error) {
    console.error('Error autosaving story:', error);
    throw error;
  }
};

// Get user's stories
export const getUserStories = async (
  userId: string,
  filters?: StoryFilters
): Promise<SavedStory[]> => {
  try {
    let q = query(
      collection(db, STORIES_COLLECTION),
      where('userId', '==', userId)
    );

    // Apply sorting
    if (filters?.sortBy) {
      q = query(q, orderBy(filters.sortBy, filters.sortOrder || 'desc'));
    } else {
      q = query(q, orderBy('updatedAt', 'desc'));
    }

    const querySnapshot = await getDocs(q);
    const stories: SavedStory[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      stories.push({
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        lastAutoSave: data.lastAutoSave?.toDate()
      } as SavedStory);
    });

    // Apply client-side filtering
    let filteredStories = stories;
    
    if (filters?.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filteredStories = filteredStories.filter(story => 
        story.topic.toLowerCase().includes(searchLower) ||
        story.ideaTitle.toLowerCase().includes(searchLower) ||
        story.ideaDescription.toLowerCase().includes(searchLower)
      );
    }

    if (filters?.tags && filters.tags.length > 0) {
      filteredStories = filteredStories.filter(story =>
        story.tags?.some(tag => filters.tags?.includes(tag))
      );
    }

    return filteredStories;
  } catch (error) {
    console.error('Error fetching user stories:', error);
    throw error;
  }
};

// Get single story
export const getStory = async (storyId: string): Promise<SavedStory | null> => {
  try {
    const storyDoc = await getDoc(doc(db, STORIES_COLLECTION, storyId));
    
    if (!storyDoc.exists()) {
      return null;
    }
    
    const data = storyDoc.data();
    return {
      ...data,
      id: storyDoc.id,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      lastAutoSave: data.lastAutoSave?.toDate()
    } as SavedStory;
  } catch (error) {
    console.error('Error fetching story:', error);
    throw error;
  }
};

// Delete story
export const deleteStory = async (storyId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, STORIES_COLLECTION, storyId));
  } catch (error) {
    console.error('Error deleting story:', error);
    throw error;
  }
};

// Toggle favorite
export const toggleStoryFavorite = async (storyId: string): Promise<void> => {
  try {
    const storyRef = doc(db, STORIES_COLLECTION, storyId);
    const storyDoc = await getDoc(storyRef);
    
    if (!storyDoc.exists()) {
      throw new Error('Story not found');
    }
    
    const currentFavorite = storyDoc.data().isFavorite || false;
    
    await updateDoc(storyRef, {
      isFavorite: !currentFavorite,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error toggling favorite:', error);
    throw error;
  }
};

// Increment view count
export const incrementViewCount = async (storyId: string): Promise<void> => {
  try {
    const storyRef = doc(db, STORIES_COLLECTION, storyId);
    await updateDoc(storyRef, {
      viewCount: increment(1)
    });
  } catch (error) {
    console.error('Error incrementing view count:', error);
  }
};

// Restore a specific version
export const restoreStoryVersion = async (
  storyId: string,
  versionId: string
): Promise<void> => {
  try {
    const storyRef = doc(db, STORIES_COLLECTION, storyId);
    const storyDoc = await getDoc(storyRef);
    
    if (!storyDoc.exists()) {
      throw new Error('Story not found');
    }
    
    const existingStory = storyDoc.data() as SavedStory;
    const versionToRestore = existingStory.versions.find(v => v.id === versionId);
    
    if (!versionToRestore) {
      throw new Error('Version not found');
    }
    
    // Create a new version that's a copy of the restored version
    const newVersionNumber = existingStory.versions.length + 1;
    const restoredVersion: StoryVersion = {
      ...versionToRestore,
      id: `v${newVersionNumber}_${Date.now()}`,
      versionNumber: newVersionNumber,
      createdAt: new Date(),
      changeDescription: `Restored from version ${versionToRestore.versionNumber}`
    };
    
    await updateDoc(storyRef, {
      currentVersion: restoredVersion,
      versions: [...existingStory.versions, restoredVersion],
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error restoring version:', error);
    throw error;
  }
};